import React from 'react';

// Controller (reports #20-#28)
import { TestSummaryTable } from './controller/TestSummaryTable.tsx';
import { FailingControlsList } from './controller/FailingControlsList.tsx';
import { ExceptionRateChart } from './controller/ExceptionRateChart.tsx';
import { SampleSizeTable } from './controller/SampleSizeTable.tsx';
import { RollforwardTable } from './controller/RollforwardTable.tsx';
import { IPEStatusPanel } from './controller/IPEStatusPanel.tsx';
import { ToDToEChart } from './controller/ToDToEChart.tsx';
import { SoDVerifiedTable } from './controller/SoDVerifiedTable.tsx';
import { EvidenceCoverageBars } from './controller/EvidenceCoverageBars.tsx';

// CAE / Manager (reports #9-#19)
import { ControlUniverseTable } from './cae/ControlUniverseTable.tsx';
import { TestingStatusTable } from './cae/TestingStatusTable.tsx';
import { IssuesPipelineKanban } from './cae/IssuesPipelineKanban.tsx';
import { CoverageMatrixTable } from './cae/CoverageMatrixTable.tsx';
import { MRCPanel } from './cae/MRCPanel.tsx';
import { ExceptionsSummaryChart } from './cae/ExceptionsSummaryChart.tsx';
import { AuditorRelianceTable } from './cae/AuditorRelianceTable.tsx';
import { ActionPlanAgingTable } from './cae/ActionPlanAgingTable.tsx';
import { ControlsByDeptChart } from './cae/ControlsByDeptChart.tsx';
import { ITGCCoverageBars } from './cae/ITGCCoverageBars.tsx';
import { TesterWorkloadPanel } from './cae/TesterWorkloadPanel.tsx';

// CFO / Executive (reports #1-#8)
import { ComplianceScoreGauge } from './cfo/ComplianceScoreGauge.tsx';
import { CoverageHeatmapGrid } from './cfo/CoverageHeatmapGrid.tsx';
import { OpenIssuesPanel } from './cfo/OpenIssuesPanel.tsx';
import { ProgramProgressPanel } from './cfo/ProgramProgressPanel.tsx';
import { SOCCoverageTable } from './cfo/SOCCoverageTable.tsx';
import { RiskLandscapeChart } from './cfo/RiskLandscapeChart.tsx';
import { RepeatEscalatedTable } from './cfo/RepeatEscalatedTable.tsx';
import { ScoreTrendChart } from './cfo/ScoreTrendChart.tsx';

// Tester "my work" (reports #30-#34, #36)
import { MyControlsList } from './tester/MyControlsList.tsx';
import { DueThisWeekStrip } from './tester/DueThisWeekStrip.tsx';
import { MyProgressDonut } from './tester/MyProgressDonut.tsx';
import { MyExceptionsList } from './tester/MyExceptionsList.tsx';
import { EvidenceChecklistGrid } from './tester/EvidenceChecklistGrid.tsx';
import { RerunsRequiredAlert } from './tester/RerunsRequiredAlert.tsx';

// External Auditor (reports #37-#41)
import { ControlInventoryTable } from './auditor/ControlInventoryTable.tsx';
import { WorkpapersPackageView } from './auditor/WorkpapersPackageView.tsx';
import { ExceptionReportTable } from './auditor/ExceptionReportTable.tsx';
import { IssueLedgerTable } from './auditor/IssueLedgerTable.tsx';
import { RelianceEligibleTable } from './auditor/RelianceEligibleTable.tsx';
import { ProcessChartingStatusTable } from './auditor/ProcessChartingStatusTable.tsx';

// Scope & Coverage (feedback round 2026-08, cross-persona)
import { ScopeControlCountsCard } from './scope/ScopeControlCountsCard.tsx';
import { ScopeKeyNonKeyDonut } from './scope/ScopeKeyNonKeyDonut.tsx';
import { ScopeControlsByRiskChart } from './scope/ScopeControlsByRiskChart.tsx';
import { ScopeRiskSummaryCard } from './scope/ScopeRiskSummaryCard.tsx';
import { ScopeRisksMappedDonut } from './scope/ScopeRisksMappedDonut.tsx';
import { ScopeSystemsCard } from './scope/ScopeSystemsCard.tsx';
import { ScopeEntitiesCard } from './scope/ScopeEntitiesCard.tsx';
import { ScopeMaterialityTable } from './scope/ScopeMaterialityTable.tsx';
import { ScopePlannedCompletedChart } from './scope/ScopePlannedCompletedChart.tsx';
import { ScopeOverdueCard } from './scope/ScopeOverdueCard.tsx';
import { ScopeTestingStatusChart } from './scope/ScopeTestingStatusChart.tsx';
import { ScopeControlDeltaCard } from './scope/ScopeControlDeltaCard.tsx';
import { ScopeRiskDeltaCard } from './scope/ScopeRiskDeltaCard.tsx';

// Tester avg-time (feedback round 2026-08)
import { TesterAvgTimeCard } from './tester/TesterAvgTimeCard.tsx';

// Reviewer (6th persona, feedback round 2026-08, reports reviewer #1-#3)
import { ReviewerQueueTable } from './reviewer/ReviewerQueueTable.tsx';
import { ReviewerSummaryCard } from './reviewer/ReviewerSummaryCard.tsx';
import { ReviewerPendingByTesterChart } from './reviewer/ReviewerPendingByTesterChart.tsx';

/** The 6 self-serve dashboard keys, matching the backend's `dashboard_key` enum
 * (`/api/dashboards/:key/layout`) and the 6 persona route groups shipped
 * under `/api/analytics/*` (5 from the original feature, plus `reviewer`
 * added in the 2026-08 feedback round). */
export type PersonaKey = 'controller' | 'cae' | 'cfo' | 'tester' | 'auditor' | 'reviewer';

export const PERSONA_LABELS: Record<PersonaKey, string> = {
  controller: 'Controller',
  cae: 'CAE / Manager',
  cfo: 'CFO / Executive',
  tester: 'Tester',
  auditor: 'External Auditor',
  reviewer: 'Reviewer',
};

export const PERSONA_KEYS: PersonaKey[] = ['controller', 'cae', 'cfo', 'tester', 'auditor', 'reviewer'];

export type WidgetSize = { w: number; h: number };

/** Coarse visual grouping used by the self-serve gallery (badge + grouping) — not a data-shape guarantee. */
export type WidgetCategory = 'kpi' | 'chart' | 'table';

export const CATEGORY_LABELS: Record<WidgetCategory, string> = {
  kpi: 'KPI',
  chart: 'Chart',
  table: 'Table',
};

export type WidgetDefinition = {
  /** Stable key, persisted in saved layouts (`WidgetInstance.widget_key`). Never rename once shipped. */
  widget_key: string;
  title: string;
  description?: string;
  /** Coarse visual category for the self-serve gallery — KPI tile, chart/graphic, or table/list. */
  category: WidgetCategory;
  /** Which persona dashboard(s) this widget belongs to by default (used to seed
   * the default layout and to group the gallery) — the gallery itself still
   * shows every widget for every persona (true self-serve pick-and-choose). */
  personas: PersonaKey[];
  Component: React.ComponentType;
  defaultSize: WidgetSize;
  minSize: WidgetSize;
};

/**
 * Single source of truth for every self-fetching report widget available in
 * the self-serve dashboard framework (Feature 5.3, wave 13). Each entry maps
 * a stable `widget_key` to the already-built persona widget component — none
 * of these take data props, they each own their own React Query hook.
 */
export const WIDGET_REGISTRY: WidgetDefinition[] = [
  // ---- Controller (#20-#28) ----
  {
    widget_key: 'controller_test_summary',
    title: 'Control Test Summary',
    description: 'One row per control: workflow status, conclusion, attributes, exceptions, confidence.',
    category: 'table',
    personas: ['controller'],
    Component: TestSummaryTable,
    defaultSize: { w: 12, h: 8 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'controller_failing_controls',
    title: 'Failing Controls',
    description: 'Alert list of controls whose latest test run concluded ineffective.',
    category: 'table',
    personas: ['controller'],
    Component: FailingControlsList,
    defaultSize: { w: 6, h: 7 },
    minSize: { w: 4, h: 4 },
  },
  {
    widget_key: 'controller_exception_rate',
    title: 'Exception Rate by Control',
    description: 'Scatter of sample size vs. average deviation rate, with the worst offenders listed below.',
    category: 'chart',
    personas: ['controller'],
    Component: ExceptionRateChart,
    defaultSize: { w: 6, h: 10 },
    minSize: { w: 4, h: 6 },
  },
  {
    widget_key: 'controller_sample_size',
    title: 'Sample Size Report',
    description: 'Sample size and deviation rate per control, flagging under-sampled controls.',
    category: 'table',
    personas: ['controller'],
    Component: SampleSizeTable,
    defaultSize: { w: 12, h: 8 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'controller_rollforward',
    title: 'Re-performance & Roll-forward Tracker',
    description: 'Re-runs and roll-forward test runs, with reason and status per attempt.',
    category: 'table',
    personas: ['controller'],
    Component: RollforwardTable,
    defaultSize: { w: 12, h: 8 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'controller_ipe_status',
    title: 'IPE Validation Status',
    description: '% of tested controls with information produced by entity (IPE) validated, plus a filterable table.',
    category: 'table',
    personas: ['controller'],
    Component: IPEStatusPanel,
    defaultSize: { w: 6, h: 8 },
    minSize: { w: 4, h: 5 },
  },
  {
    widget_key: 'controller_tod_toe',
    title: 'ToD vs ToE Breakdown',
    description: 'Donut of test-of-design vs. test-of-effectiveness run mix, with a filterable run list.',
    category: 'chart',
    personas: ['controller'],
    Component: ToDToEChart,
    defaultSize: { w: 6, h: 8 },
    minSize: { w: 4, h: 5 },
  },
  {
    widget_key: 'controller_sod_verified',
    title: 'Segregation of Duties Verified',
    description: 'Preparer/reviewer pairs per control, flagging any without verified segregation of duties.',
    category: 'table',
    personas: ['controller'],
    Component: SoDVerifiedTable,
    defaultSize: { w: 6, h: 7 },
    minSize: { w: 4, h: 4 },
  },
  {
    widget_key: 'controller_evidence_coverage',
    title: 'Evidence Coverage Map',
    description: 'Bar-per-control view of % of tested samples with evidence attached.',
    category: 'chart',
    personas: ['controller'],
    Component: EvidenceCoverageBars,
    defaultSize: { w: 6, h: 7 },
    minSize: { w: 4, h: 4 },
  },

  // ---- CAE / Manager (#9-#19) ----
  {
    widget_key: 'cae_control_universe',
    title: 'Control Universe Inventory',
    description: 'Every control in the org, with type, frequency, significance, and reliance flags.',
    category: 'table',
    personas: ['cae'],
    Component: ControlUniverseTable,
    defaultSize: { w: 12, h: 8 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'cae_testing_status',
    title: 'Testing Status by Control',
    description: 'Assignee, due date, status, and pass/fail counts per control.',
    category: 'table',
    personas: ['cae'],
    Component: TestingStatusTable,
    defaultSize: { w: 12, h: 8 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'cae_issues_pipeline',
    title: 'Issues Pipeline',
    description: 'Kanban board of open issues grouped by workflow status, with action-plan progress.',
    category: 'table',
    personas: ['cae'],
    Component: IssuesPipelineKanban,
    defaultSize: { w: 12, h: 8 },
    minSize: { w: 6, h: 5 },
  },
  {
    widget_key: 'cae_coverage_matrix',
    title: 'Coverage Matrix',
    description: 'Heat table of controls mapped per risk, with unmapped risks highlighted.',
    category: 'table',
    personas: ['cae'],
    Component: CoverageMatrixTable,
    defaultSize: { w: 6, h: 8 },
    minSize: { w: 4, h: 5 },
  },
  {
    widget_key: 'cae_mrc',
    title: 'Multi-Reliance Control (MRC) Panel',
    description: 'Management Review Control counts and pass/fail status, filterable by testing frequency.',
    category: 'table',
    personas: ['cae'],
    Component: MRCPanel,
    defaultSize: { w: 6, h: 8 },
    minSize: { w: 4, h: 5 },
  },
  {
    widget_key: 'cae_exceptions_summary',
    title: 'Exceptions Summary',
    description: 'Bar chart of top controls by exception count, with expandable detail rows.',
    category: 'chart',
    personas: ['cae'],
    Component: ExceptionsSummaryChart,
    defaultSize: { w: 12, h: 7 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'cae_auditor_reliance',
    title: 'External Auditor Reliance',
    description: 'Controls flagged for external auditor reliance, with test status.',
    category: 'table',
    personas: ['cae'],
    Component: AuditorRelianceTable,
    defaultSize: { w: 12, h: 7 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'cae_action_plan_aging',
    title: 'Action Plan Aging',
    description: 'Overdue action plans, color-coded by days overdue (0-30 / 31-60 / 60+).',
    category: 'table',
    personas: ['cae'],
    Component: ActionPlanAgingTable,
    defaultSize: { w: 12, h: 7 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'cae_controls_by_dept',
    title: 'Controls by Department',
    description: 'Grouped bar chart of total / active / key controls per department, with a table drill-in.',
    category: 'chart',
    personas: ['cae'],
    Component: ControlsByDeptChart,
    defaultSize: { w: 6, h: 8 },
    minSize: { w: 4, h: 4 },
  },
  {
    widget_key: 'cae_itgc_coverage',
    title: 'ITGC Coverage',
    description: 'Stacked bars of controls tested vs. total per ITGC domain.',
    category: 'chart',
    personas: ['cae'],
    Component: ITGCCoverageBars,
    defaultSize: { w: 6, h: 7 },
    minSize: { w: 4, h: 4 },
  },
  {
    widget_key: 'cae_tester_workload',
    title: 'Tester Workload',
    description: 'Assigned vs. completed test runs per tester, with a bar chart and next-due table.',
    category: 'chart',
    personas: ['cae'],
    Component: TesterWorkloadPanel,
    defaultSize: { w: 12, h: 7 },
    minSize: { w: 6, h: 4 },
  },

  // ---- CFO / Executive (#1-#8) ----
  {
    widget_key: 'cfo_compliance_score',
    title: 'Compliance Score',
    description: 'Org-level SOX compliance posture — the single executive KPI.',
    category: 'kpi',
    personas: ['cfo'],
    Component: ComplianceScoreGauge,
    defaultSize: { w: 12, h: 7 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'cfo_coverage_heatmap',
    title: 'Control Coverage Heatmap',
    description: 'Colour-coded grid of active vs. total controls per SOX cycle.',
    category: 'chart',
    personas: ['cfo'],
    Component: CoverageHeatmapGrid,
    defaultSize: { w: 6, h: 7 },
    minSize: { w: 4, h: 4 },
  },
  {
    widget_key: 'cfo_open_issues',
    title: 'Open Issues',
    description: 'Open, escalated, and repeat issue KPI tiles plus a department/classification breakdown.',
    category: 'kpi',
    personas: ['cfo'],
    Component: OpenIssuesPanel,
    defaultSize: { w: 12, h: 9 },
    minSize: { w: 6, h: 6 },
  },
  {
    widget_key: 'cfo_program_progress',
    title: 'Program Progress Summary',
    description: 'Overall testing completion rate with a workflow/conclusion donut.',
    category: 'chart',
    personas: ['cfo'],
    Component: ProgramProgressPanel,
    defaultSize: { w: 6, h: 8 },
    minSize: { w: 4, h: 4 },
  },
  {
    widget_key: 'cfo_soc_coverage',
    title: 'SOC Report Coverage',
    description: 'SOC1/SOC2 report coverage per key system, with a bridge-letter check column.',
    category: 'table',
    personas: ['cfo'],
    Component: SOCCoverageTable,
    defaultSize: { w: 12, h: 7 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'cfo_risk_landscape',
    title: 'Risk Landscape',
    description: 'Risk counts by classification and risk level — stacked bar plus a donut summary.',
    category: 'chart',
    personas: ['cfo'],
    Component: RiskLandscapeChart,
    defaultSize: { w: 12, h: 7 },
    minSize: { w: 6, h: 5 },
  },
  {
    widget_key: 'cfo_repeat_escalated',
    title: 'Repeat & Escalated Issues',
    description: 'Issues flagged repeat or escalated, with vintage count.',
    category: 'table',
    personas: ['cfo'],
    Component: RepeatEscalatedTable,
    defaultSize: { w: 12, h: 7 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'cfo_score_trend',
    title: 'Compliance Score Trend',
    description: '12-month compliance score line chart with Healthy / Watch / Critical band zones.',
    category: 'chart',
    personas: ['cfo'],
    Component: ScoreTrendChart,
    defaultSize: { w: 12, h: 8 },
    minSize: { w: 6, h: 4 },
  },

  // ---- Tester "my work" (#30-#34, #36) ----
  {
    widget_key: 'tester_my_controls',
    title: 'My Controls',
    description: 'Your assigned tests, soonest due first.',
    category: 'table',
    personas: ['tester'],
    Component: MyControlsList,
    defaultSize: { w: 12, h: 8 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'tester_due_this_week',
    title: 'Due This Week',
    description: '7-day calendar strip of how many of your controls are due each day, plus an overdue bucket.',
    category: 'kpi',
    personas: ['tester'],
    Component: DueThisWeekStrip,
    defaultSize: { w: 12, h: 5 },
    minSize: { w: 6, h: 3 },
  },
  {
    widget_key: 'tester_my_progress',
    title: 'My Progress',
    description: 'Donut of your current test runs by workflow status, plus a completion % KPI.',
    category: 'chart',
    personas: ['tester'],
    Component: MyProgressDonut,
    defaultSize: { w: 6, h: 7 },
    minSize: { w: 4, h: 4 },
  },
  {
    widget_key: 'tester_reruns_required',
    title: 'Re-runs Required',
    description: 'Your controls flagged for a re-test or roll-forward, with a start-re-run action.',
    category: 'table',
    personas: ['tester'],
    Component: RerunsRequiredAlert,
    defaultSize: { w: 6, h: 7 },
    minSize: { w: 4, h: 4 },
  },
  {
    widget_key: 'tester_my_exceptions',
    title: 'My Exceptions',
    description: 'Expandable exception list across your assigned test runs, filterable by severity.',
    category: 'table',
    personas: ['tester'],
    Component: MyExceptionsList,
    defaultSize: { w: 12, h: 7 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'tester_evidence_checklist',
    title: 'Evidence Checklist',
    description: 'Per-sample evidence review status for a selected test run.',
    category: 'table',
    personas: ['tester'],
    Component: EvidenceChecklistGrid,
    defaultSize: { w: 12, h: 8 },
    minSize: { w: 6, h: 4 },
  },

  // ---- External Auditor (#37-#41) ----
  {
    widget_key: 'auditor_control_inventory',
    title: 'Control Inventory for Reliance',
    description: 'Every control flagged for external auditor reliance, with an Excel export.',
    category: 'table',
    personas: ['auditor'],
    Component: ControlInventoryTable,
    defaultSize: { w: 12, h: 8 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'auditor_workpapers_package',
    title: 'Workpapers Package',
    description: 'Execution summary and attribute-level findings for a selected control.',
    category: 'table',
    personas: ['auditor'],
    Component: WorkpapersPackageView,
    defaultSize: { w: 12, h: 8 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'auditor_exception_report',
    title: 'Exception Report',
    description: 'Sample-level exceptions across current test runs, severity-colored.',
    category: 'table',
    personas: ['auditor'],
    Component: ExceptionReportTable,
    defaultSize: { w: 12, h: 7 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'auditor_issue_ledger',
    title: 'Issue Ledger',
    description: 'Issue register with period, classification, linked controls, and action-plan completion.',
    category: 'table',
    personas: ['auditor'],
    Component: IssueLedgerTable,
    defaultSize: { w: 12, h: 7 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'auditor_reliance_eligible',
    title: 'Reliance-Eligible Controls',
    description: 'Scoping aid for external auditor reliance decisions.',
    category: 'table',
    personas: ['auditor'],
    Component: RelianceEligibleTable,
    defaultSize: { w: 12, h: 7 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'auditor_process_charting',
    title: 'Process Charting Status',
    description: 'Charted vs. uncharted processes by SOX cycle — a process is "charted" once it has a current flowchart version.',
    category: 'table',
    personas: ['auditor'],
    Component: ProcessChartingStatusTable,
    defaultSize: { w: 12, h: 8 },
    minSize: { w: 6, h: 4 },
  },

  // ---- Scope & Coverage (feedback round 2026-08, cross-persona) ----
  {
    widget_key: 'scope_control_counts',
    title: 'Control Counts by Scope',
    description: 'Total controls in scope, key vs. non-key, with a period-over-period delta.',
    category: 'kpi',
    personas: ['cfo', 'cae', 'controller'],
    Component: ScopeControlCountsCard,
    defaultSize: { w: 6, h: 6 },
    minSize: { w: 4, h: 4 },
  },
  {
    widget_key: 'scope_key_nonkey',
    title: 'Key vs. Non-Key Controls',
    description: 'Donut of in-scope controls flagged key vs. non-key.',
    category: 'chart',
    personas: ['cfo'],
    Component: ScopeKeyNonKeyDonut,
    defaultSize: { w: 6, h: 8 },
    minSize: { w: 4, h: 4 },
  },
  {
    widget_key: 'scope_controls_by_risk',
    title: 'Controls by Risk Level',
    description: 'In-scope controls grouped by mapped risk level (High/Medium/Low/Unrated).',
    category: 'chart',
    personas: ['cae'],
    Component: ScopeControlsByRiskChart,
    defaultSize: { w: 6, h: 7 },
    minSize: { w: 4, h: 4 },
  },
  {
    widget_key: 'scope_risk_summary',
    title: 'Risk Summary',
    description: 'Total risks in scope, mapped vs. unmapped, with a period-over-period delta.',
    category: 'kpi',
    personas: ['cae'],
    Component: ScopeRiskSummaryCard,
    defaultSize: { w: 6, h: 6 },
    minSize: { w: 4, h: 4 },
  },
  {
    widget_key: 'scope_risks_mapped',
    title: 'Risks Mapped to Controls',
    description: 'Donut of risk coverage — mapped vs. unmapped risks.',
    category: 'chart',
    personas: ['cae'],
    Component: ScopeRisksMappedDonut,
    defaultSize: { w: 6, h: 8 },
    minSize: { w: 4, h: 4 },
  },
  {
    widget_key: 'scope_systems',
    title: 'Systems in Scope',
    description: 'Count of systems currently in scope.',
    category: 'kpi',
    personas: ['cfo', 'cae'],
    Component: ScopeSystemsCard,
    defaultSize: { w: 4, h: 5 },
    minSize: { w: 3, h: 3 },
  },
  {
    widget_key: 'scope_entities',
    title: 'Entities in Scope',
    description: 'Count of legal entities / business units currently in scope.',
    category: 'kpi',
    personas: ['cfo', 'cae'],
    Component: ScopeEntitiesCard,
    defaultSize: { w: 4, h: 5 },
    minSize: { w: 3, h: 3 },
  },
  {
    widget_key: 'scope_materiality',
    title: 'Materiality Assessments',
    description: 'Materiality thresholds and benchmark per assessment cycle.',
    category: 'table',
    personas: ['cfo'],
    Component: ScopeMaterialityTable,
    defaultSize: { w: 12, h: 7 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'scope_planned_completed',
    title: 'Planned vs. Completed',
    description: 'Testing progress against the planned/scheduled population.',
    category: 'chart',
    personas: ['cfo', 'auditor'],
    Component: ScopePlannedCompletedChart,
    defaultSize: { w: 6, h: 7 },
    minSize: { w: 4, h: 4 },
  },
  {
    widget_key: 'scope_overdue',
    title: 'Overdue Testing & PBC',
    description: 'Past-due test runs and evidence requests, testing and PBC side by side.',
    category: 'kpi',
    personas: ['cae'],
    Component: ScopeOverdueCard,
    defaultSize: { w: 6, h: 6 },
    minSize: { w: 4, h: 4 },
  },
  {
    widget_key: 'scope_testing_status',
    title: 'Testing Status Rollup',
    description: 'Donut of current test runs by workflow status (draft/in progress/completed/reviewed).',
    category: 'chart',
    personas: ['cfo', 'auditor'],
    Component: ScopeTestingStatusChart,
    defaultSize: { w: 6, h: 8 },
    minSize: { w: 4, h: 4 },
  },
  {
    widget_key: 'scope_control_delta',
    title: 'Control Count Δ',
    description: 'Period-over-period change in total controls in scope vs. the prior sweep.',
    category: 'kpi',
    personas: ['cfo'],
    Component: ScopeControlDeltaCard,
    defaultSize: { w: 4, h: 5 },
    minSize: { w: 3, h: 3 },
  },
  {
    widget_key: 'scope_risk_delta',
    title: 'Risk Count Δ',
    description: 'Period-over-period change in total risks in scope vs. the prior sweep.',
    category: 'kpi',
    personas: ['cfo'],
    Component: ScopeRiskDeltaCard,
    defaultSize: { w: 4, h: 5 },
    minSize: { w: 3, h: 3 },
  },

  // ---- Tester avg-time (feedback round 2026-08) ----
  {
    widget_key: 'tester_avg_time',
    title: 'Average Test Time',
    description: 'Average days to complete a test, org-wide and per tester.',
    category: 'kpi',
    personas: ['tester'],
    Component: TesterAvgTimeCard,
    defaultSize: { w: 6, h: 6 },
    minSize: { w: 4, h: 4 },
  },

  // ---- Reviewer (6th persona, feedback round 2026-08) ----
  {
    widget_key: 'reviewer_queue',
    title: 'Review Queue',
    description: 'Pending-review runs, oldest first.',
    category: 'table',
    personas: ['reviewer'],
    Component: ReviewerQueueTable,
    defaultSize: { w: 12, h: 8 },
    minSize: { w: 6, h: 4 },
  },
  {
    widget_key: 'reviewer_summary',
    title: 'Review Summary',
    description: 'Pending vs. reviewed run counts, and average time waiting for review.',
    category: 'kpi',
    personas: ['reviewer'],
    Component: ReviewerSummaryCard,
    defaultSize: { w: 6, h: 6 },
    minSize: { w: 4, h: 4 },
  },
  {
    widget_key: 'reviewer_pending_by_tester',
    title: 'Pending Review by Tester',
    description: 'Runs awaiting review, grouped by the tester who submitted them.',
    category: 'chart',
    personas: ['reviewer'],
    Component: ReviewerPendingByTesterChart,
    defaultSize: { w: 6, h: 8 },
    minSize: { w: 4, h: 4 },
  },
];

const WIDGET_BY_KEY: Record<string, WidgetDefinition> = WIDGET_REGISTRY.reduce(
  (acc, w) => {
    acc[w.widget_key] = w;
    return acc;
  },
  {} as Record<string, WidgetDefinition>
);

export function getWidgetDefinition(widgetKey: string): WidgetDefinition | undefined {
  return WIDGET_BY_KEY[widgetKey];
}

/** Default widget set for a persona dashboard key — every registry widget whose `personas` includes it. */
export function getDefaultWidgetsForPersona(persona: PersonaKey): WidgetDefinition[] {
  return WIDGET_REGISTRY.filter((w) => w.personas.includes(persona));
}
